import React, { useState, useEffect, useCallback } from 'react';
import { getUserEvents, createEvent, updateEvent, deleteEvent, listSharedEvents, shareEventWithFriend } from '../actions/events';
import { getFriends, sendFriendRequest, sendAppInvitation } from '../actions/friends';
import { getUserProfile } from '../actions/users';
import { TextField, Button, Typography, Box, Card, CardContent, Grid2, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, useTheme, Chip, Avatar, CircularProgress, Skeleton } from '@mui/material';
import { Add, Edit, Delete, Share, Event, Schedule, Info, AttachMoney, PersonAdd as PersonAddIcon, Email as EmailIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useAlert } from '../contexts/AlertContext';
import { cardGridSx, cardItemSx } from '../utils/cardLayout';
import PlanningPulse from '../components/PlanningPulse';
import { dateBounds, validPlanningDate } from '../utils/planningDates';

const Home = () => {
  const [events, setEvents] = useState([]);
  const [sharedEvents, setSharedEvents] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [newEvent, setNewEvent] = useState({ title: '', description: '', date: '' });
  const [openEventDialog, setOpenEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [friends, setFriends] = useState([]);
  const [selectedFriend, setSelectedFriend] = useState('');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [userProfile, setUserProfile] = useState({ name: '', picture_url: ''});
  
  // Loading states
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingSharedEvents, setLoadingSharedEvents] = useState(true);
  const [loadingFriends, setLoadingFriends] = useState(true);
  const [loadingUserProfile, setLoadingUserProfile] = useState(true);
  const [savingEvent, setSavingEvent] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(false);
  const [sharingEvent, setSharingEvent] = useState(false);
  const [addFriendDialogOpen, setAddFriendDialogOpen] = useState(false);
  const [invitationDialogOpen, setInvitationDialogOpen] = useState(false);
  const [newFriendEmail, setNewFriendEmail] = useState('');
  const [invitationEmail, setInvitationEmail] = useState('');
  const [sendingInvitation, setSendingInvitation] = useState(false);
  const [sendingFriendRequest, setSendingFriendRequest] = useState(false);
  
  const userName = Cookies.get('user');
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const theme = useTheme();

  const fetchFriends = useCallback(async () => {
    setLoadingFriends(true);
    try {
      const friendsList = await getFriends();
      setFriends(friendsList);
    } catch (err) {
      showAlert('Failed to fetch friends.', 'error');
    } finally {
      setLoadingFriends(false);
    }
  }, [showAlert]);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoadingEvents(true);
      try {
        const fetchedEvents = await getUserEvents();
        setEvents(fetchedEvents);
      } catch (err) {
        showAlert('Failed to fetch events.', 'error');
      } finally {
        setLoadingEvents(false);
      }
    };
    fetchEvents();

    const fetchSharedEvents = async () => {
      setLoadingSharedEvents(true);
      try {
        const fetchedSharedEvents = await listSharedEvents();
        setSharedEvents(fetchedSharedEvents);
      } catch (err) {
        showAlert('Failed to fetch shared events.', 'error');
      } finally {
        setLoadingSharedEvents(false);
      }
    };
    fetchSharedEvents();

    fetchFriends();

    const fetchUserProfile = async () => {
      setLoadingUserProfile(true);
      try {
        const profile = await getUserProfile();
        const userData = profile?.data || profile;
        setUserProfile(userData);
      } catch (err) {
        console.error('Failed to fetch user profile', err);
      } finally {
        setLoadingUserProfile(false);
      }
    };
    fetchUserProfile();
  }, [fetchFriends]);

  const handleOpenShareDialog = (event) => {
    setEditingEvent(event);
    setShareDialogOpen(true);
    fetchFriends();
  };

  const handleAddFriendFromShare = async () => {
    setSendingFriendRequest(true);
    try {
      await sendFriendRequest(newFriendEmail);
      setNewFriendEmail('');
      setAddFriendDialogOpen(false);
      await fetchFriends();
      showAlert('Friend request sent! Once they accept, you can share this event with them.', 'success');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      if (errorMessage?.includes('not found') || errorMessage?.includes('not registered') || errorMessage?.includes('User not found')) {
        showAlert(errorMessage, 'error');
        setInvitationEmail(newFriendEmail);
        setAddFriendDialogOpen(false);
        setInvitationDialogOpen(true);
      } else {
        showAlert(errorMessage || 'Error sending friend request', 'error');
      }
    } finally {
      setSendingFriendRequest(false);
    }
  };

  const handleSendInvitationFromShare = async () => {
    setSendingInvitation(true);
    try {
      await sendAppInvitation(invitationEmail);
      setInvitationEmail('');
      setInvitationDialogOpen(false);
      showAlert('Invitation sent! Once they register and accept your friend request, you can share events with them.', 'success');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Error sending invitation';
      showAlert(errorMessage, 'error');
    } finally {
      setSendingInvitation(false);
    }
  };

  const handleEventClick = (eventId) => {
    navigate(`/events/${eventId}/budget`);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEvent({ ...newEvent, [name]: value });
  };

  const toggleEventDialog = () => {
    setOpenEventDialog((prev) => !prev);
  };

  const handleSaveEvent = async () => {
    setSavingEvent(true);
    try {
      if (!validPlanningDate(newEvent.date)) {
        showAlert('Choose a real date between 2000 and ten years from today.', 'error');
        return;
      }

      if (editingEvent) {
        await updateEvent(editingEvent.id, newEvent);
        setEvents(events.map((event) => (event.id === editingEvent.id ? { ...event, ...newEvent } : event)));
        setSharedEvents(sharedEvents.map((event) => (event.id === editingEvent.id ? { ...event, ...newEvent } : event)));
        showAlert('Event updated successfully!', 'success');
      } else {
        const event = await createEvent(newEvent);
        setEvents([...events, event]);
        showAlert('Event created successfully!', 'success');
        navigate(`/events/${event.id}/setup`);
      }
  
      setNewEvent({ title: '', description: '', date: '' });
      setEditingEvent(null);
      toggleEventDialog();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to save event.', 'error');
    } finally {
      setSavingEvent(false);
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setNewEvent({ title: event.title, description: event.description, date: event.date });
    toggleEventDialog();
  };

  const handleDeleteEvent = (event) => {
    setEditingEvent(event);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteEvent = async () => {
    setDeletingEvent(true);
    try {
      await deleteEvent(editingEvent.id);
      setEvents(events.filter((event) => event.id !== editingEvent.id));
      setEditingEvent(null);
      setDeleteDialogOpen(false);
      showAlert('Event deleted successfully!', 'success');
    } catch (err) {
      showAlert('Failed to delete event.', 'error');
    } finally {
      setDeletingEvent(false);
    }
  };

  const handleShareEvent = async () => {
    if (!selectedFriend) {
      showAlert('Please select a friend to share the event with.', 'error');
      return;
    }
    setSharingEvent(true);
    try {
      await shareEventWithFriend(editingEvent.id, selectedFriend);
      setShareDialogOpen(false);
      setSelectedFriend('');
      showAlert('Event shared successfully!', 'success');
    } catch (err) {
      showAlert('Failed to share event.', 'error');
    } finally {
      setSharingEvent(false);
    }
  };

  const capitalizeName = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleSelectEvent = (eventId) => {
    setSelectedEvents((prev) =>
      prev.includes(eventId) ? prev.filter((id) => id !== eventId) : [...prev, eventId]
    );
  };

  const handleViewSelectedEvents = () => {
    if (selectedEvents.length === 0) {
      showAlert('Please select at least one event to view.', 'error');
      return;
    }
  
    if (selectedEvents.length === 1) {
      navigate(`/events/${selectedEvents[0]}/budget`);
    } else {
      const eventIds = selectedEvents.join(',');
      navigate(`/events/multi-view/budget?eventIds=${eventIds}`);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: theme.palette.mode === 'dark' ? '#121212' : '#ffffff',
      p: { xs: 1, sm: 2, md: 3 }
    }}>
      {/* Header Section */}
      <Box sx={{ 
        textAlign: 'center', 
        mb: 2,
        py: 1,
        px: { xs: 1, sm: 2 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
          {loadingUserProfile ? (
            <>
              <Skeleton
                variant="circular"
                width={{ xs: 60, sm: 80, md: 100 }}
                height={{ xs: 60, sm: 80, md: 100 }}
                sx={{ 
                  mr: { xs: 0, sm: 3 },
                  mb: { xs: 2, sm: 0 },
                }}
              />
              <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                <Skeleton variant="text" width={300} height={60} sx={{ mb: 2 }} />
                <Skeleton variant="text" width={250} height={40} sx={{ mb: 3 }} />
              </Box>
            </>
          ) : (
            <>
              {userProfile.picture_url && (
                <Avatar
                  src={userProfile.picture_url}
                  alt={userProfile.name || userName}
                  sx={{ 
                    width: { xs: 60, sm: 80, md: 100 }, 
                    height: { xs: 60, sm: 80, md: 100 }, 
                    mr: { xs: 0, sm: 3 },
                    mb: { xs: 2, sm: 0 },
                    border: `3px solid ${theme.palette.primary.main}`,
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 4px 20px rgba(0, 0, 0, 0.3)'
                      : '0 4px 20px rgba(25, 118, 210, 0.2)',
                  }}
                />
              )}
              <Box sx={{ 
                textAlign: userProfile.picture_url ? { xs: 'center', sm: 'left' } : 'center' 
              }}>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    mb: 0.5,
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
                  }}
                >
                  Welcome back, {capitalizeName(userProfile.name || userName)}! 👋
                </Typography>
                <Typography 
                  variant="h6" 
                  color="text.secondary"
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  Manage your events and stay organized
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <PlanningPulse hideWhenEmpty compactHome />
      {/* Action Buttons - Side by Side on Mobile */}
      <Box sx={{ 
        mb: 4,
        display: 'flex',
        justifyContent: { xs: 'center', sm: 'flex-start' },
        flexWrap: 'wrap',
        gap: 2
      }}>
        {/* Create New Event Button */}
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => {
            setEditingEvent(null);
            setNewEvent({ title: '', description: '', date: '' });
            toggleEventDialog();
          }}
          sx={{ 
            flex: selectedEvents.length > 0 ? { xs: '1 1 calc(50% - 8px)', sm: '0 0 auto' } : { xs: '0 0 auto', sm: '0 0 auto' },
            minWidth: { xs: '140px', sm: '180px' },
            px: { xs: 2, sm: 4 },
            py: 1.5,
            borderRadius: 2,
            fontSize: { xs: '0.8rem', sm: '1rem' },
            fontWeight: 600,
            '&:hover': {
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          Create New Event
        </Button>

        {/* View Selected Events Button - Only show when events are selected */}
        {selectedEvents.length > 0 && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Event />}
            onClick={handleViewSelectedEvents}
            sx={{ 
              flex: { xs: '1 1 calc(50% - 8px)', sm: '0 0 auto' },
              minWidth: { xs: '140px', sm: '180px' },
              px: { xs: 2, sm: 4 },
              py: 1.5,
              borderRadius: 2,
              fontSize: { xs: '0.8rem', sm: '1rem' },
              fontWeight: 600,
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderColor: theme.palette.primary.main,
              '&:hover': {
                transform: 'translateY(-2px)',
                backgroundColor: theme.palette.primary.dark,
                color: theme.palette.primary.contrastText,
              },
              transition: 'all 0.3s ease'
            }}
          >
            View Selected ({selectedEvents.length})
          </Button>
        )}
      </Box>

      {/* Your Events Section */}
      <Box sx={{ mb: 6 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 600,
            mb: 3,
            color: theme.palette.text.primary,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
            flexWrap: 'wrap'
          }}
        >
          <Event sx={{ color: theme.palette.primary.main }} />
          Your Events
          <Chip 
            label={loadingEvents ? '...' : events.length} 
            color="primary" 
            size="small" 
            sx={{ ml: 2 }}
          />
        </Typography>

        {loadingEvents ? (
          <Box sx={cardGridSx(260, { xs: 2, sm: 3 })}>
            {[1, 2, 3, 4, 5, 6].map((index) => (
              <Box key={index} sx={cardItemSx()}>
                <Card sx={{ 
                  height: '100%',
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                    : '0 4px 20px rgba(0, 0, 0, 0.1)',
                }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Skeleton variant="text" width="80%" height={32} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="60%" height={24} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="70%" height={24} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="100%" height={48} sx={{ mb: 3 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2 }}>
                      <Skeleton variant="circular" width={24} height={24} />
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        ) : events.length === 0 ? (
          <Card sx={{ 
            p: { xs: 3, sm: 4 }, 
            textAlign: 'center',
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0, 0, 0, 0.2)'
              : '0 4px 20px rgba(0, 0, 0, 0.1)',
          }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              No events yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              Get started by creating your first event. Click "Create New Event" to begin planning!
            </Typography>
          </Card>
        ) : (
          <Box sx={cardGridSx(260, { xs: 2, sm: 3 })}>
            {events.map((event) => (
              <Box key={event.id} sx={cardItemSx()}>
                <Card
                  sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    background: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                      : '0 4px 20px rgba(0, 0, 0, 0.1)',
                    border: theme.palette.mode === 'dark'
                      ? '1px solid rgba(255, 255, 255, 0.1)'
                      : '1px solid rgba(255, 255, 255, 0.2)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 8px 30px rgba(0, 0, 0, 0.3)'
                        : '0 8px 30px rgba(0, 0, 0, 0.15)',
                    }
                  }}
                  onClick={(e) => {
                    if (e.target.type !== 'checkbox') {
                      handleEventClick(event.id);
                    }
                  }}
                >
                  <CardContent 
                    onClick={() => handleEventClick(event.id)} 
                    sx={{ p: { xs: 2, sm: 3 } }}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontSize: { xs: '1.1rem', sm: '1.25rem' },
                        mb: 2
                      }}
                    >
                      {event.title}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Schedule sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.secondary.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        {new Date(event.date).toLocaleDateString()}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <AttachMoney sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.secondary.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Budget: ${event.total_budget ? parseFloat(event.total_budget).toFixed(2) : '0.00'}
                      </Typography>
                    </Box>

                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        mb: 3
                      }}
                    >
                      {event.description || 'No description provided'}
                    </Typography>

                    {/* Action buttons */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      pt: 2,
                      borderTop: `1px solid ${theme.palette.divider}`
                    }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedEvents.includes(event.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleSelectEvent(event.id);
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            size="small"
                          />
                        }
                        label=""
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      />

                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit Event" arrow>
                          <IconButton 
                            size="small"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              handleEditEvent(event); 
                            }}
                            sx={{ 
                              color: theme.palette.primary.main,
                              '&:hover': { backgroundColor: theme.palette.primary.main + '20' }
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Share Event" arrow>
                          <IconButton 
                            size="small"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              handleOpenShareDialog(event);
                            }}
                            sx={{ 
                              color: theme.palette.secondary.main,
                              '&:hover': { backgroundColor: theme.palette.secondary.main + '20' }
                            }}
                          >
                            <Share fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Event" arrow>
                          <IconButton 
                            size="small"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              handleDeleteEvent(event); 
                            }}
                            sx={{ 
                              color: theme.palette.error.main,
                              '&:hover': { backgroundColor: theme.palette.error.main + '20' }
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Shared Events Section */}
      <Box sx={{ mb: 6 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 600,
            mb: 3,
            color: theme.palette.text.primary,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
            flexWrap: 'wrap'
          }}
        >
          <Share sx={{ color: theme.palette.secondary.main }} />
          Shared Events
          <Chip 
            label={loadingSharedEvents ? '...' : sharedEvents.length} 
            color="secondary" 
            size="small" 
            sx={{ ml: 2 }}
          />
        </Typography>

        {loadingSharedEvents ? (
          <Box sx={cardGridSx(260, { xs: 2, sm: 3 })}>
            {[1, 2, 3].map((index) => (
              <Box key={index} sx={cardItemSx()}>
                <Card sx={{ 
                  height: '100%',
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                    : '0 4px 20px rgba(0, 0, 0, 0.1)',
                }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Skeleton variant="text" width="80%" height={32} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="60%" height={24} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="70%" height={24} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="100%" height={48} sx={{ mb: 3 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2 }}>
                      <Skeleton variant="circular" width={24} height={24} />
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        ) : sharedEvents.length === 0 ? (
          <Card sx={{ 
            p: { xs: 3, sm: 4 }, 
            textAlign: 'center',
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0, 0, 0, 0.2)'
              : '0 4px 20px rgba(0, 0, 0, 0.1)',
          }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              No shared events
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              When your friends share events with you, they will appear here.
            </Typography>
          </Card>
        ) : (
          <Box sx={cardGridSx(260, { xs: 2, sm: 3 })}>
            {sharedEvents.map((event) => (
              <Box key={event.id} sx={cardItemSx()}>
                <Card sx={{ 
                  height: '100%',
                  cursor: 'pointer',
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                    : '0 4px 20px rgba(0, 0, 0, 0.1)',
                  border: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(255, 255, 255, 0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 8px 30px rgba(0, 0, 0, 0.3)'
                      : '0 8px 30px rgba(0, 0, 0, 0.15)',
                  }
                }}>
                  <CardContent 
                    onClick={() => handleEventClick(event.id)} 
                    sx={{ p: { xs: 2, sm: 3 } }}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontSize: { xs: '1.1rem', sm: '1.25rem' },
                        mb: 2
                      }}
                    >
                      {event.title}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Schedule sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.secondary.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        {new Date(event.date).toLocaleDateString()}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <AttachMoney sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.secondary.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Budget: ${event.total_budget ? parseFloat(event.total_budget).toFixed(2) : '0.00'}
                      </Typography>
                    </Box>

                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        mb: 3
                      }}
                    >
                      {event.description || 'No description provided'}
                    </Typography>

                    {/* Action buttons */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      pt: 2,
                      borderTop: `1px solid ${theme.palette.divider}`
                    }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedEvents.includes(event.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleSelectEvent(event.id);
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            size="small"
                          />
                        }
                        label=""
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      />

                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {/* Empty box to maintain layout consistency */}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Event Dialogs */}
      <Dialog open={openEventDialog} onClose={toggleEventDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ 
          fontWeight: 600,
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
        }}>
          {editingEvent ? 'Edit Event' : 'Create a New Event'}
        </DialogTitle>
        <DialogContent sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.02)'
            : 'rgba(255, 255, 255, 0.9)',
          pt: 2
        }}>
          <TextField
            label="Event Title"
            name="title"
            fullWidth
            margin="normal"
            value={newEvent.title}
            onChange={handleInputChange}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
          <TextField
            label="Description"
            name="description"
            fullWidth
            margin="normal"
            value={newEvent.description}
            onChange={handleInputChange}
            multiline
            rows={3}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
          <TextField
            label="Date"
            name="date"
            fullWidth
            margin="normal"
            value={newEvent.date}
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={handleInputChange}
            type="date"
            inputProps={dateBounds()}
            helperText="Between 2000 and ten years from today."
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
          p: { xs: 1, sm: 2 },
          gap: { xs: 1, sm: 0 },
          flexDirection: 'row',
          alignItems: 'center'
        }}>
          <Button 
            onClick={toggleEventDialog} 
            color="error"
            disabled={savingEvent}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEvent} 
            color="primary"
            variant="contained"
            disabled={savingEvent}
            startIcon={savingEvent ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ borderRadius: 2 }}
          >
            {savingEvent ? 'Saving...' : (editingEvent ? 'Save Changes' : 'Create Event')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Event Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Event</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{editingEvent?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          p: { xs: 1, sm: 2 },
          gap: { xs: 1, sm: 0 },
          flexDirection: 'row',
          alignItems: 'center'
        }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            color="primary"
            disabled={deletingEvent}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteEvent} 
            color="error"
            variant="contained"
            disabled={deletingEvent}
            startIcon={deletingEvent ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ borderRadius: 2 }}
          >
            {deletingEvent ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Event Dialog */}
      <Dialog 
        open={shareDialogOpen} 
        onClose={() => {
          setShareDialogOpen(false);
          setSelectedFriend('');
        }} 
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: { xs: 0, sm: 3 },
            background: theme.palette.mode === 'dark' 
              ? 'rgba(30, 30, 30, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            m: { xs: 0, sm: 2 },
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 600,
          pb: 1,
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 },
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center'
        }}>
          <Share sx={{ mr: 2, color: theme.palette.primary.main }} />
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            Share Event with Friends
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ 
          pt: { xs: 2, sm: 3 }, 
          pb: 2,
          px: { xs: 2, sm: 3 },
          flex: 1,
          overflow: 'auto'
        }}>
          {/* Event Information Card */}
          {editingEvent && (
            <Card sx={{
              mb: { xs: 3, sm: 4 },
              mt: { xs: 1, sm: 2 },
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                : '0 4px 20px rgba(0, 0, 0, 0.1)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(255, 255, 255, 0.2)',
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Event sx={{ mr: 2, color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600, 
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}>
                    Event Details
                  </Typography>
                </Box>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Event Title
                    </Typography>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      mb: 2,
                      fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}>
                      {editingEvent.title}
                    </Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Date
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Schedule sx={{ mr: 1, fontSize: 18, color: theme.palette.text.secondary }} />
                      <Typography variant="body1" sx={{ 
                        fontWeight: 500,
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}>
                        {new Date(editingEvent.date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </Typography>
                    </Box>
                  </Grid2>
                  {editingEvent.description && (
                    <Grid2 size={{ xs: 12 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Description
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        lineHeight: 1.6,
                        fontSize: { xs: '0.875rem', sm: '0.875rem' }
                      }}>
                        {editingEvent.description}
                      </Typography>
                    </Grid2>
                  )}
                </Grid2>
              </CardContent>
            </Card>
          )}

          {/* Friend Selection Section */}
          <Box sx={{ mb: { xs: 1, sm: 3 } }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              mb: { xs: 2, sm: 3 }, 
              display: 'flex', 
              alignItems: 'center',
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}>
              <Info sx={{ mr: 2, color: theme.palette.info.main }} />
              Select Friends to Share With
            </Typography>
            
            {loadingFriends ? (
              <Skeleton variant="rectangular" height={100} />
            ) : friends.length === 0 ? (
              <Card sx={{
                textAlign: 'center',
                py: { xs: 4, sm: 6 },
                px: { xs: 2, sm: 4 },
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.03)'
                  : 'rgba(255, 255, 255, 0.7)',
                borderRadius: 3,
                border: theme.palette.mode === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(0, 0, 0, 0.1)',
              }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ 
                    mb: 2, 
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}>
                    No Friends Available
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ 
                    mb: 3, 
                    lineHeight: 1.6,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}>
                    Add friends or invite someone to join the app. Once they register and accept your request, you can share events with them.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PersonAddIcon />}
                      onClick={() => setAddFriendDialogOpen(true)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        py: 1,
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}
                    >
                      Add Friend
                    </Button>
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<Share />}
                      onClick={() => setInvitationDialogOpen(true)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        py: 1,
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}
                    >
                      Invite Friend
                    </Button>
                  </Box>
                </Box>
              </Card>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ 
                  mb: 3, 
                  lineHeight: 1.6,
                  fontSize: { xs: '0.875rem', sm: '0.875rem' }
                }}>
                  Choose a friend from your network to share this event. They'll be able to view and collaborate on event planning.
                </Typography>
                
                <FormControl fullWidth>
                  <InputLabel id="friend-select-label">Select Friend</InputLabel>
                  <Select
                    labelId="friend-select-label"
                    value={selectedFriend}
                    onChange={(e) => setSelectedFriend(e.target.value)}
                    label="Select Friend"
                    sx={{
                      borderRadius: 2,
                      '& .MuiSelect-select': {
                        py: 2
                      }
                    }}
                  >
                    {friends.map((friend) => (
                      <MenuItem key={friend.id} value={friend.id} sx={{ py: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: theme.palette.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: 'white'
                          }}>
                            {friend.name.charAt(0).toUpperCase()}
                          </Box>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {capitalizeName(friend.name)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {friend.email}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedFriend && (
                  <Box sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    background: theme.palette.mode === 'dark' 
                      ? 'rgba(76, 175, 80, 0.1)'
                      : 'rgba(76, 175, 80, 0.05)',
                    border: `1px solid ${theme.palette.success.main}30`
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      lineHeight: 1.5,
                      fontSize: { xs: '0.875rem', sm: '0.875rem' }
                    }}>
                      <strong>Ready to share!</strong> Your selected friend will receive access to view and collaborate on this event.
                    </Typography>
                  </Box>
                )}

                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  mt: 3,
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(33, 150, 243, 0.1)'
                    : 'rgba(33, 150, 243, 0.05)',
                  border: `1px solid ${theme.palette.info.main}30`,
                  flexWrap: 'wrap'
                }}>
                  <Info sx={{ color: theme.palette.info.main, fontSize: 20 }} />
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.info.main, mb: 0.5 }}>
                      Don't see your friend?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      Add an existing user or invite someone new to join the app.
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<PersonAddIcon />}
                      onClick={() => setAddFriendDialogOpen(true)}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                      Add Friend
                    </Button>
                    <Button
                      variant="outlined"
                      color="success"
                      size="small"
                      startIcon={<Share />}
                      onClick={() => setInvitationDialogOpen(true)}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                      Invite Friend
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{
          p: { xs: 2, sm: 3 },
          pt: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          gap: 2,
          flexDirection: 'row',
          justifyContent: 'flex-end',
          alignItems: 'center'
        }}>
          <Button 
            onClick={() => {
              setShareDialogOpen(false);
              setSelectedFriend('');
            }} 
            color="inherit"
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              py: 1.5,
              px: { xs: 2, sm: 3 },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minWidth: { xs: 80, sm: 100 }
            }}
          >
            Cancel
          </Button>
          {friends.length > 0 && (
            <Button 
              onClick={handleShareEvent} 
              color="primary"
              variant="contained"
              disabled={!selectedFriend || sharingEvent}
              startIcon={sharingEvent ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                py: 1.5,
                px: { xs: 3, sm: 4 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minWidth: { xs: 100, sm: 120 }
              }}
            >
              {sharingEvent ? 'Sharing...' : 'Share Event'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Add Friend Dialog (from Share Event) */}
      <Dialog
        open={addFriendDialogOpen}
        onClose={() => setAddFriendDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            background: theme.palette.mode === 'dark'
              ? 'rgba(30, 30, 30, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <PersonAddIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Add a Friend
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            p: 2,
            mb: 3,
            mt: 2,
            borderRadius: 2,
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(33, 150, 243, 0.1)'
              : 'rgba(33, 150, 243, 0.05)',
            border: `1px solid ${theme.palette.info.main}30`
          }}>
            <Info sx={{ color: theme.palette.info.main, mt: 0.5, fontSize: 20 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.info.main, mb: 1 }}>
                Account Required
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Your friend must already have an Event Planner account. Once they accept your request, you can share events with them.
              </Typography>
            </Box>
          </Box>

          <TextField
            label="Friend's Email"
            value={newFriendEmail}
            onChange={(e) => setNewFriendEmail(e.target.value)}
            variant="outlined"
            fullWidth
            margin="normal"
            type="email"
            placeholder="Enter your friend's email address"
            helperText="Enter the email address they used to create their account"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            mt: 2,
            borderRadius: 2,
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(76, 175, 80, 0.1)'
              : 'rgba(76, 175, 80, 0.05)',
            border: `1px solid ${theme.palette.success.main}30`
          }}>
            <Share sx={{ color: theme.palette.success.main, fontSize: 20 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.success.main, mb: 0.5 }}>
                Friend not registered?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Invite them to join the app instead.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="success"
              size="small"
              startIcon={<Share />}
              onClick={() => {
                setInvitationEmail(newFriendEmail);
                setAddFriendDialogOpen(false);
                setInvitationDialogOpen(true);
              }}
              disabled={!newFriendEmail}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Invite
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setAddFriendDialogOpen(false)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddFriendFromShare}
            variant="contained"
            disabled={!newFriendEmail || sendingFriendRequest}
            startIcon={sendingFriendRequest ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            {sendingFriendRequest ? 'Sending...' : 'Send Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Friend Dialog (from Share Event) */}
      <Dialog
        open={invitationDialogOpen}
        onClose={() => setInvitationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            background: theme.palette.mode === 'dark'
              ? 'rgba(30, 30, 30, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <EmailIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Invite Friend to Join
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            p: 2,
            mb: 3,
            mt: 2,
            borderRadius: 2,
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(76, 175, 80, 0.1)'
              : 'rgba(76, 175, 80, 0.05)',
            border: `1px solid ${theme.palette.success.main}30`
          }}>
            <Info sx={{ color: theme.palette.success.main, mt: 0.5, fontSize: 20 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.success.main, mb: 1 }}>
                Send Invitation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Your friend will receive an email to join Event Planner. Once they register and become your friend, come back here to share this event.
              </Typography>
            </Box>
          </Box>

          <TextField
            label="Friend's Email"
            value={invitationEmail}
            onChange={(e) => setInvitationEmail(e.target.value)}
            variant="outlined"
            fullWidth
            margin="normal"
            type="email"
            placeholder="Enter your friend's email address"
            helperText="We'll send them an invitation to join the app"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setInvitationDialogOpen(false)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSendInvitationFromShare}
            variant="contained"
            color="success"
            disabled={!invitationEmail || sendingInvitation}
            startIcon={sendingInvitation ? <CircularProgress size={16} color="inherit" /> : <Share />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            {sendingInvitation ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Home;
